import { useEffect, useState } from 'react';
import { Form, Input, Button, Space, Select, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import ApiService from '../../core/service/api-service';
import StudyTaskTypeEnum from '../../enums/StudyTaskTypeEnum';
import {
    RE_ASSESSMENT_REASONS_FALLBACK,
    RE_ASSESSMENT_REASON_OTHER,
} from '../../enums/ReAssessmentEnums';
import { matchesSearch } from '../../core/utils/text-search';

/**
 * Yeniden değerlendirme talebi formu.
 *
 * İki alan: gerekçe (statik liste, son madde "Diğer" → serbest metin) ve atanacak doktor.
 * Doktorun varsayılanı raporun OKUYANIDIR; aynı değer sunucuda study_task.original_read_user_id'ye
 * de yazılır ve istatistiğin gruplama anahtarı olur.
 *
 * Doktor alanı ZORUNLU DEĞİL: varsayılan seçili gelir ama temizlenebilir. Boş gönderilirse talep
 * atamasız açılır; çekimin mevcut doktor ataması olduğu gibi kalır. İstatistiğin anahtarı
 * original_read_user_id olduğu için atamasız talepler de kırılıma girer.
 *
 * Gerekçe listesi sunucudan gelir (tek kaynak backend'deki ReAssessmentTexts); istek başarısız
 * olursa yereldeki yedek kullanılır.
 */
export default function ReAssessmentRequestModal({ studyId, requestId, onSaved, onCancel }) {
    const { t } = useTranslation();
    const [form] = Form.useForm();

    const [loading, setLoading] = useState(false);
    const [reasons, setReasons] = useState(RE_ASSESSMENT_REASONS_FALLBACK);
    const [users, setUsers] = useState([]);
    // Varsayılan gerekçe "Diğer": talepler çoğunlukla listedeki standart maddelerin dışında
    // kalıyor, bu yüzden serbest metin alanı baştan açık gelir.
    const [isOther, setIsOther] = useState(true);

    const init = async () => {
        const [defaults, readable] = await Promise.all([
            ApiService.StudyTask.GetReAssessmentDefaults(studyId, requestId),
            ApiService.UserManager.GetReadableUsers(),
        ]);

        let options = readable?.success ? (readable.data || []) : [];

        if (defaults?.success) {
            if (defaults.data.reasons?.length) {
                setReasons(defaults.data.reasons);
            }

            const defaultId = defaults.data.assignedUserId;

            // Okuyan doktor, "rapor okuyabilen kullanıcılar" listesinde olmayabilir (yetkisi
            // sonradan alınmış olabilir); seçim boş görünmesin diye listeye eklenir.
            if (defaultId && !options.some(x => x.id === defaultId)) {
                options = [
                    { id: defaultId, visibleName: defaults.data.assignedUserName || defaultId },
                    ...options,
                ];
            }

            form.setFieldsValue({ assignedUserId: defaultId ?? undefined });
        }

        setUsers(options);
    };

    useEffect(() => {
        init();
    }, [studyId, requestId]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const description = values.reason === RE_ASSESSMENT_REASON_OTHER
                ? values.customReason
                : values.reason;

            const response = await ApiService.StudyTask.Create({
                studyId,
                requestId: requestId ?? null,
                taskType: StudyTaskTypeEnum.RE_ASSESSMENT,
                description,
                assignedUserId: values.assignedUserId ?? null,
            });

            if (response.success) {
                onSaved && onSaved();
            }
        } catch (error) {
            console.error('Validation failed:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Form
            form={form}
            layout="vertical"
            style={{ padding: '16px 0' }}
            initialValues={{ reason: RE_ASSESSMENT_REASON_OTHER }}
        >
            <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message={t('The report confirmation will be removed and the study will be reassigned to the selected doctor.')}
                description={t('If no doctor is selected, the current assignment of the study stays unchanged.')}
            />

            <Form.Item
                name="reason"
                label={t('Re-Assessment Reason')}
                rules={[{ required: true, message: t('Please select a reason') }]}
            >
                <Select
                    placeholder={t('Select a reason')}
                    options={reasons.map(x => ({ value: x, label: x }))}
                    onChange={(value) => setIsOther(value === RE_ASSESSMENT_REASON_OTHER)}
                />
            </Form.Item>

            {isOther && (
                <Form.Item
                    name="customReason"
                    label={t('Description')}
                    rules={[{ required: true, message: t('Please enter description') }]}
                >
                    <Input.TextArea rows={3} placeholder={t('Enter re-assessment reason')} />
                </Form.Item>
            )}

            {/* Atama zorunlu degil: alan raporun okuyaniyla dolu gelir, kullanici temizlerse
                talep atamasiz acilir ve cekimin doktor atamasi degismez. */}
            <Form.Item
                name="assignedUserId"
                label={t('Assigned Doctor')}
                extra={t('Leave empty to request re-assessment without assigning a doctor')}
            >
                <Select
                    showSearch
                    allowClear
                    options={users}
                    fieldNames={{ label: 'visibleName', value: 'id' }}
                    filterOption={(input, option) => matchesSearch(option?.visibleName, input)}
                    placeholder={t('No doctor assigned')}
                />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                <Space>
                    <Button onClick={onCancel}>{t('Cancel')}</Button>
                    <Button type="primary" onClick={handleSubmit} loading={loading}>
                        {t('Submit')}
                    </Button>
                </Space>
            </Form.Item>
        </Form>
    );
}
